import { Link } from 'react-router-dom'
import Icon from '../components/Icon'
import Logo from '../components/Logo'

const STATS = [
  { value: '8+', label: 'Industries supported' },
  { value: '20+', label: 'Issue categories' },
  { value: '60%', label: 'Less admin overhead' },
  { value: '24/7', label: 'Always-on reporting' },
]

const HOW_IT_WORKS = [
  {
    icon: 'photo_camera',
    title: '1. Snap',
    text: 'Take a photo of the issue. Location and timestamp are attached — with your permission — so responders can find it fast.',
  },
  {
    icon: 'neurology',
    title: '2. AI identifies',
    text: 'Computer vision categorizes the issue and assesses severity instantly, so nothing gets lost in a generic inbox.',
  },
  {
    icon: 'send',
    title: '3. Routed to the right team',
    text: 'The report lands on the responsible organization’s dashboard for immediate action and scheduling.',
  },
]

const INDUSTRIES = [
  { icon: 'remove_road', label: 'Roads & Infrastructure' },
  { icon: 'water_drop', label: 'Utilities & Water' },
  { icon: 'park', label: 'Parks & Recreation' },
  { icon: 'delete', label: 'Waste & Recycling' },
  { icon: 'health_and_safety', label: 'Public Safety' },
  { icon: 'eco', label: 'Environment' },
  { icon: 'apartment', label: 'Facilities & Property' },
  { icon: 'pedal_bike', label: 'Cycling & Pedestrian' },
]

const REPORTER_FEATURES = [
  { icon: 'photo_camera', title: 'Snap and send in seconds', text: 'A guided four-step flow — photo, category, location, done.' },
  { icon: 'my_location', title: 'Pin the exact spot', text: 'Drop a pin on the map so crews go straight to the problem.' },
  { icon: 'notifications_active', title: 'Track progress', text: 'Follow your report from submitted to resolved, with status updates.' },
  { icon: 'lock', title: 'Privacy first', text: 'Location is only used with your permission. Your contact details are never sold or shared.' },
]

const ORG_FEATURES = [
  'Centralized dashboard & workflows',
  'Real-time analytics & heatmaps',
  'AI triage & duplicate detection',
  'API integration ready',
]

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-surface-dim text-text-primary">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-surface-dark/95 backdrop-blur-md border-b border-border-subtle">
        <div className="max-w-6xl mx-auto h-16 px-4 md:px-10 flex items-center justify-between">
          <Link to="/" aria-label="Snagmatic home" className="flex items-center">
            <Logo height={30} />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-label-md text-text-secondary">
            <a href="#how-it-works" className="hover:text-accent transition-colors">How it Works</a>
            <a href="#industries" className="hover:text-accent transition-colors">Industries</a>
            <a href="#for-organizations" className="hover:text-accent transition-colors">For Organizations</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-label-md text-text-secondary hover:text-accent transition-colors">
              Sign in
            </Link>
            <Link
              to="/report/new"
              className="bg-accent text-surface-dark font-semibold text-label-md px-5 py-2.5 rounded-full hover:bg-accent-light active:scale-95 transition"
            >
              Report an Issue
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 md:px-10 pt-20 md:pt-28 pb-14 text-center">
        <div className="max-w-3xl mx-auto space-y-6">
          <span className="inline-flex items-center gap-2 bg-surface border border-border text-label-sm text-text-secondary px-4 py-1.5 rounded-full">
            <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
            An edge4ward Pty Ltd product
          </span>

          <h1 className="text-headline-lg md:text-[46px] md:leading-[54px] font-bold tracking-tight">
            See it. Snap it. <em className="text-accent">Sorted.</em>
          </h1>

          <p className="text-body-lg text-text-secondary max-w-xl mx-auto">
            Snagmatic is the professional-grade reporting platform that connects people who spot
            problems — potholes, leaks, hazards, faults — with the organizations responsible for
            fixing them. In seconds, not weeks.
          </p>

          <div className="flex items-center justify-center gap-3 flex-wrap pt-2">
            <Link
              to="/report/new"
              className="inline-flex items-center gap-2 bg-accent text-surface-dark font-semibold text-label-md px-7 py-3.5 rounded-xl shadow-lg shadow-accent/20 hover:bg-accent-light active:scale-95 transition"
            >
              <Icon name="add_circle" className="text-xl" />
              Report an Issue
            </Link>
            <a
              href="#for-organizations"
              className="inline-flex items-center gap-2 border border-border text-text-primary text-label-md px-7 py-3.5 rounded-xl hover:bg-surface-high transition"
            >
              <Icon name="corporate_fare" className="text-xl" />
              Request a Demo
            </a>
          </div>

          <p className="text-label-sm text-text-muted">
            Free for every reporter. No app store required — works on any device.
          </p>
        </div>
      </section>

      {/* Stats strip */}
      <section className="px-4 md:px-10 pb-16">
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3">
          {STATS.map((s) => (
            <div key={s.label} className="bg-surface-low border border-border rounded-xl p-4 text-center">
              <p className="text-headline-md text-accent">{s.value}</p>
              <p className="text-label-sm text-text-muted">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="bg-surface-low border-y border-border px-4 md:px-10 py-16 md:py-20">
        <div className="max-w-5xl mx-auto space-y-10">
          <div className="text-center space-y-2">
            <h2 className="text-headline-lg-mobile md:text-headline-lg">How it works</h2>
            <p className="text-body-md text-text-secondary">Three steps from problem to fixed.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {HOW_IT_WORKS.map((item) => (
              <div key={item.title} className="bg-surface border border-border rounded-xl p-6 space-y-4">
                <div className="inline-flex bg-accent/15 border border-accent/20 p-3 rounded-xl">
                  <Icon name={item.icon} className="text-accent text-2xl" />
                </div>
                <h3 className="text-headline-md">{item.title}</h3>
                <p className="text-body-md text-text-secondary">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section id="industries" className="px-4 md:px-10 py-16 md:py-20">
        <div className="max-w-5xl mx-auto space-y-10">
          <div className="text-center space-y-2">
            <h2 className="text-headline-lg-mobile md:text-headline-lg">One platform, every industry</h2>
            <p className="text-body-md text-text-secondary max-w-xl mx-auto">
              The same simple reporting experience, whether the issue belongs to a city, a utility,
              a facility manager, or a field-services team.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {INDUSTRIES.map((ind) => (
              <div
                key={ind.label}
                className="bg-surface border border-border rounded-xl p-5 text-center space-y-3 hover:border-accent/50 transition-colors"
              >
                <Icon name={ind.icon} className="text-accent text-3xl" />
                <p className="text-label-md text-text-primary">{ind.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For reporters */}
      <section className="bg-surface-low border-y border-border px-4 md:px-10 py-16 md:py-20">
        <div className="max-w-5xl mx-auto space-y-10">
          <div className="text-center space-y-2">
            <h2 className="text-headline-lg-mobile md:text-headline-lg">Built for the people who notice</h2>
            <p className="text-body-md text-text-secondary">Reporting should take seconds, not paperwork.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {REPORTER_FEATURES.map((f) => (
              <div key={f.title} className="flex gap-4 bg-surface border border-border rounded-xl p-5">
                <div className="bg-accent/15 border border-accent/20 p-2.5 rounded-xl self-start shrink-0">
                  <Icon name={f.icon} className="text-accent text-xl" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-label-md text-text-primary">{f.title}</h3>
                  <p className="text-body-md text-text-secondary">{f.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For organizations */}
      <section id="for-organizations" className="px-4 md:px-10 py-16 md:py-20">
        <div className="max-w-5xl mx-auto bg-surface-low border border-border rounded-xl p-6 md:p-12 space-y-6">
          <p className="text-label-sm font-bold text-accent uppercase tracking-widest">
            Government &amp; Enterprise
          </p>
          <h2 className="text-headline-lg-mobile md:text-headline-lg max-w-lg">
            Turn public eyes into your early-warning system.
          </h2>
          <p className="text-body-md text-text-secondary max-w-xl">
            Reduce administrative overhead by up to 60%. Snagmatic gives your teams a centralized
            dashboard for intake, triage, workflows, and citizen communication — across every asset
            class you manage.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
            {ORG_FEATURES.map((f) => (
              <div key={f} className="flex items-center gap-2 text-body-md text-text-primary">
                <Icon name="check_circle" className="text-accent text-xl" />
                {f}
              </div>
            ))}
          </div>
          <a
            href="mailto:hello@edge4ward.com?subject=Snagmatic%20Partner%20Demo"
            className="inline-flex border border-accent text-accent text-label-md px-6 py-3 rounded-xl hover:bg-accent/10 transition"
          >
            Request Partner Demo
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-surface-dark border-t border-border-subtle px-4 md:px-10 py-12">
        <div className="max-w-5xl mx-auto space-y-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1 space-y-3">
              <Logo height={24} />
              <p className="text-label-sm text-text-muted">
                The reliable issue-reporting platform for modern cities, utilities, and enterprises.
              </p>
              <p className="text-label-sm text-text-muted">
                A product of <span className="text-text-secondary font-semibold">edge4ward Pty Ltd</span>.
              </p>
            </div>
            <FooterCol
              title="Product"
              links={[
                { label: 'How it works', href: '#how-it-works' },
                { label: 'Industries', href: '#industries' },
                { label: 'For Organizations', href: '#for-organizations' },
              ]}
            />
            <FooterCol
              title="Legal"
              links={[
                { label: 'Privacy Policy', to: '/privacy' },
                { label: 'Terms of Service', to: '/terms' },
              ]}
            />
            <FooterCol
              title="Company"
              links={[
                { label: 'edge4ward Pty Ltd', href: 'mailto:hello@edge4ward.com' },
                { label: 'hello@edge4ward.com', href: 'mailto:hello@edge4ward.com' },
              ]}
            />
          </div>
          <div className="border-t border-border-subtle pt-6 flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-label-sm text-text-muted">
              © 2026 edge4ward Pty Ltd. All rights reserved.
            </p>
            <p className="text-label-sm text-text-muted">Snagmatic® is a trademark of edge4ward Pty Ltd.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

function FooterCol({ title, links }) {
  return (
    <div className="space-y-3">
      <h4 className="text-label-md font-semibold text-text-primary">{title}</h4>
      <ul className="space-y-2">
        {links.map((l) => (
          <li key={l.label}>
            {l.to ? (
              <Link to={l.to} className="text-label-sm text-text-secondary hover:text-accent transition-colors">
                {l.label}
              </Link>
            ) : (
              <a href={l.href} className="text-label-sm text-text-secondary hover:text-accent transition-colors">
                {l.label}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
